import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Route } from "lucide-react";

import { QcIncidentIssueTiersFields } from "@/components/qc/QcIncidentIssueTiersFields";
import { QcIncidentNotesThread } from "@/components/qc/QcIncidentNotesThread";
import { QcIncidentSummary } from "@/components/qc/QcIncidentSummary";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { CardTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useIntegratorMe } from "@/lib/me-query";
import { EM_DASH } from "@/lib/utils";
import {
  QC_INCIDENTS_QUERY_KEY,
  QC_ROOT_CAUSES_QUERY_KEY,
  POTTING_REWORK_QUERY_KEY,
  createRootCause,
  resolveIncident,
  routePottingRework,
  type QcIncidentDetail,
  type QcIncidentRepairReturnGuidance,
  type QcIncidentResolution,
  type RootCause,
} from "@/pages/manufacturing/qcIncidentsApi";
import { ClampQcReQcPanel } from "@/pages/clampqc/ClampQcReQcPanel";

type PottingReworkRoutePath = "scs" | "internal";
type QcIncidentResolutionDraft = QcIncidentResolution | "";

const NEW_ROOT_CAUSE_VALUE = "__new_root_cause__";

function filterRootCauseOptions(options: SearchableSelectOption[], query: string): SearchableSelectOption[] {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) {
    return options;
  }
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(normalized) || option.value.toLowerCase().includes(normalized),
  );
}

function todayText(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDateTime(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function regradeGradeLabel(value: string | null): string {
  if (value == null) return "Not graded";
  if (value === "Fail") return "Fail";
  return value;
}

function RepairReturnCallout({ guidance }: { guidance: QcIncidentRepairReturnGuidance }): JSX.Element {
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-950">
      <p className="font-semibold">Send unit to {guidance.destinationStationName}</p>
      <p className="mt-2 whitespace-pre-line">{guidance.detail}</p>
      {guidance.hubPath ? (
        <p className="mt-2">
          <Link
            to={guidance.hubPath}
            className="font-medium text-emerald-900 underline underline-offset-2 hover:text-emerald-950"
          >
            Open {guidance.destinationStationName}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

export function QcIncidentResolvePanel({
  incident,
  rootCauses,
  writesAllowed,
  onClose,
  onResolved,
  omitSummary = false,
  omitNotes = false,
}: {
  incident: QcIncidentDetail;
  rootCauses: RootCause[];
  writesAllowed: boolean;
  onClose: () => void;
  onResolved?: (guidance: QcIncidentRepairReturnGuidance | null) => void;
  omitSummary?: boolean;
  omitNotes?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const [repairScrap, setRepairScrap] = useState<QcIncidentResolutionDraft>(incident.repairScrap ?? "");
  const [resolutionDate, setResolutionDate] = useState(incident.resolutionDate ?? todayText());
  const [resolutionNotes, setResolutionNotes] = useState(incident.resolutionNotes ?? "");
  const [rootCausePickerValue, setRootCausePickerValue] = useState(incident.rootCause ?? "");
  const [rootCauseSearch, setRootCauseSearch] = useState("");
  const [newRootCauseDraft, setNewRootCauseDraft] = useState("");
  const [rootCauseAddError, setRootCauseAddError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedRepairGuidance, setSavedRepairGuidance] = useState<QcIncidentRepairReturnGuidance | null>(null);
  const [reworkRoutePath, setReworkRoutePath] = useState<PottingReworkRoutePath>("scs");

  const unresolvedPottingRework = incident.pottingRework != null && incident.pottingRework.regradePottingGrade == null;
  const currentUserIdentity =
    meQuery.data?.user.displayName?.trim() || meQuery.data?.user.email || "Signed-in user";
  const resolutionPersonDisplay =
    incident.status === "resolved" ? incident.resolutionPerson || EM_DASH : currentUserIdentity;

  const repairPreviewGuidance =
    repairScrap === "repair" && incident.status !== "resolved" && savedRepairGuidance == null
      ? incident.repairReturnGuidancePreview ?? null
      : null;

  const rootCauseDropdownOptions = useMemo(() => {
    const notSelected: SearchableSelectOption = { value: "", label: "Not selected" };
    const newRootCause: SearchableSelectOption = { value: NEW_ROOT_CAUSE_VALUE, label: "New root cause…" };
    const existing = rootCauses.map((row) => ({
      value: row.cause,
      label: row.display_name ?? row.cause,
    }));
    const filtered = filterRootCauseOptions(existing, rootCauseSearch);
    const query = rootCauseSearch.trim().toLowerCase();
    const leading: SearchableSelectOption[] = [];
    if (query.length === 0 || notSelected.label.toLowerCase().includes(query)) {
      leading.push(notSelected);
    }
    if (query.length === 0 || newRootCause.label.toLowerCase().includes(query)) {
      leading.push(newRootCause);
    }
    return [...leading, ...filtered];
  }, [rootCauses, rootCauseSearch]);

  const selectedRootCauseLabel = useMemo(() => {
    if (rootCausePickerValue === "" || rootCausePickerValue === NEW_ROOT_CAUSE_VALUE) {
      return null;
    }
    const match = rootCauses.find((row) => row.cause === rootCausePickerValue);
    return match?.display_name ?? match?.cause ?? incident.rootCause ?? rootCausePickerValue;
  }, [incident.rootCause, rootCausePickerValue, rootCauses]);

  const showNewRootCauseSection =
    incident.status !== "resolved" &&
    savedRepairGuidance == null &&
    rootCausePickerValue === NEW_ROOT_CAUSE_VALUE;

  const addRootCauseMutation = useMutation({
    mutationFn: (label: string) => createRootCause(label),
    onSuccess: async (row) => {
      setRootCauseAddError(null);
      setNewRootCauseDraft("");
      setRootCausePickerValue(row.cause);
      setRootCauseSearch("");
      await queryClient.invalidateQueries({ queryKey: QC_ROOT_CAUSES_QUERY_KEY });
    },
    onError: (mutationError: Error) => setRootCauseAddError(mutationError.message),
  });

  const resolveMutation = useMutation({
    mutationFn: () => {
      if (repairScrap !== "repair" && repairScrap !== "scrap" && repairScrap !== "touch_sample") {
        throw new Error("Select a resolution before saving.");
      }
      if (rootCausePickerValue === NEW_ROOT_CAUSE_VALUE) {
        throw new Error("Add the new root cause before saving the resolution.");
      }
      return resolveIncident(incident.id, {
        repair_scrap: repairScrap,
        resolution_date: resolutionDate || null,
        resolution_notes: resolutionNotes.trim() || null,
        root_cause: rootCausePickerValue || null,
      });
    },
    onSuccess: async (result) => {
      setError(null);
      setSavedRepairGuidance(result.repairReturnGuidance ?? null);
      await queryClient.invalidateQueries({ queryKey: QC_INCIDENTS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: POTTING_REWORK_QUERY_KEY });
      onResolved?.(result.repairReturnGuidance ?? null);
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  const pottingMutation = useMutation({
    mutationFn: () => routePottingRework(incident.id, reworkRoutePath),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: QC_INCIDENTS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: POTTING_REWORK_QUERY_KEY });
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  const submitDisabled =
    incident.status === "resolved" ||
    unresolvedPottingRework ||
    !writesAllowed ||
    repairScrap === "" ||
    rootCausePickerValue === NEW_ROOT_CAUSE_VALUE ||
    resolveMutation.isPending ||
    savedRepairGuidance != null;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <CardTitleWithHelp hoverHint="Resolution is separate from opening the incident. Save repair, scrap, or touch sample only after the incident has reached its final outcome. Touch sample uses the device or PCBA already on the incident; missing clamp serial or MCU UUID is fine.">
          Resolve incident
        </CardTitleWithHelp>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardBody className="space-y-4">
        {omitSummary ? null : <QcIncidentSummary incident={incident} />}
        <QcIncidentIssueTiersFields
          incident={incident}
          writesAllowed={writesAllowed}
          showTier1Editor={false}
          showTier2Editor
        />
        {omitNotes ? null : <QcIncidentNotesThread incidentId={incident.id} writesAllowed={writesAllowed} />}

        {error ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>
        ) : null}

        {savedRepairGuidance ? <RepairReturnCallout guidance={savedRepairGuidance} /> : null}

        {incident.type === "clamp" &&
        (savedRepairGuidance != null ||
          (incident.status === "resolved" && incident.repairScrap === "repair")) ? (
          <ClampQcReQcPanel />
        ) : null}

        {incident.pottingRework ? (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            Routed to {incident.pottingRework.internalSend ? "internal" : "SCS"} potting rework{" "}
            {formatDateTime(incident.pottingRework.routedAt)} by{" "}
            {incident.pottingRework.routedBy || "unknown"}. Return grade:{" "}
            <span className="font-medium">{regradeGradeLabel(incident.pottingRework.regradePottingGrade)}</span>.
          </div>
        ) : incident.type === "device" && incident.status === "unresolved" ? (
          <ReadOnlyHoverLayer
            active={!writesAllowed}
            hoverHint="Your role has read-only access to the Manufacturing hub module."
          >
            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Send rework to</p>
                <p className="mt-1 text-xs text-slate-600">
                  Choose SCS vendor potting (Megaventory purchase order) or in-house potting at SGS.
                </p>
              </div>
              <div className="inline-flex rounded-md border border-slate-300 bg-white p-0.5">
                <button
                  type="button"
                  className={`rounded px-3 py-1.5 text-sm font-medium ${
                    reworkRoutePath === "scs" ? "bg-brand-600 text-white" : "text-slate-700"
                  }`}
                  onClick={() => setReworkRoutePath("scs")}
                  disabled={!writesAllowed || pottingMutation.isPending}
                >
                  SCS
                </button>
                <button
                  type="button"
                  className={`rounded px-3 py-1.5 text-sm font-medium ${
                    reworkRoutePath === "internal" ? "bg-brand-600 text-white" : "text-slate-700"
                  }`}
                  onClick={() => setReworkRoutePath("internal")}
                  disabled={!writesAllowed || pottingMutation.isPending}
                >
                  Internal
                </button>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => pottingMutation.mutate()}
                isLoading={pottingMutation.isPending}
                disabled={!writesAllowed}
              >
                <Route className="h-4 w-4" aria-hidden />
                Route to potting rework
              </Button>
            </div>
          </ReadOnlyHoverLayer>
        ) : null}

        <ReadOnlyHoverLayer
          active={!writesAllowed}
          hoverHint="Your role has read-only access to the Manufacturing hub module."
          layout="block"
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              resolveMutation.mutate();
            }}
          >
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Resolution</Label>
                <select
                  value={repairScrap}
                  disabled={!writesAllowed || incident.status === "resolved" || savedRepairGuidance != null}
                  onChange={(event) => setRepairScrap(event.target.value as QcIncidentResolutionDraft)}
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500"
                >
                  <option value="">Select resolution…</option>
                  <option value="repair">Repair</option>
                  <option value="scrap">Scrap</option>
                  {incident.type === "device" || incident.type === "pcba" ? (
                    <option value="touch_sample">Touch sample</option>
                  ) : null}
                </select>
              </div>
              <div>
                <Label>Resolution date</Label>
                <Input
                  type="date"
                  value={resolutionDate}
                  disabled={!writesAllowed || incident.status === "resolved" || savedRepairGuidance != null}
                  onChange={(event) => setResolutionDate(event.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Resolution person</Label>
                <div className="mt-1 flex h-9 w-full items-center rounded-md border border-slate-300 bg-slate-100 px-3 text-sm text-slate-700">
                  {resolutionPersonDisplay}
                </div>
                {incident.status !== "resolved" ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Display name when available; saved as your Hub login email.
                  </p>
                ) : null}
              </div>
              <div>
                <Label>Root cause</Label>
                <SearchableSelect
                  className="mt-1"
                  value={rootCausePickerValue}
                  selectedLabelOverride={selectedRootCauseLabel}
                  disabled={!writesAllowed || incident.status === "resolved" || savedRepairGuidance != null}
                  onValueChange={(option) => {
                    setRootCausePickerValue(option?.value ?? "");
                    setRootCauseSearch("");
                    if (option?.value !== NEW_ROOT_CAUSE_VALUE) {
                      setNewRootCauseDraft("");
                      setRootCauseAddError(null);
                    }
                  }}
                  options={rootCauseDropdownOptions}
                  searchValue={rootCauseSearch}
                  onSearchChange={setRootCauseSearch}
                  placeholder="Search root causes…"
                  searchPlaceholder="Search root causes…"
                  emptyMessage="No matching root causes."
                  triggerClassName="h-9"
                />
              </div>
            </div>

            {showNewRootCauseSection ? (
              <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-3">
                <Label htmlFor="qc-incident-new-root-cause">New root cause for everyone</Label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    id="qc-incident-new-root-cause"
                    value={newRootCauseDraft}
                    onChange={(event) => setNewRootCauseDraft(event.target.value)}
                    placeholder="Describe the root cause"
                    maxLength={120}
                    disabled={!writesAllowed || addRootCauseMutation.isPending}
                    autoComplete="off"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={
                      !writesAllowed ||
                      addRootCauseMutation.isPending ||
                      newRootCauseDraft.trim() === ""
                    }
                    isLoading={addRootCauseMutation.isPending}
                    onClick={() => {
                      const trimmed = newRootCauseDraft.trim();
                      if (trimmed === "") {
                        return;
                      }
                      setRootCauseAddError(null);
                      addRootCauseMutation.mutate(trimmed);
                    }}
                  >
                    Add root cause
                  </Button>
                </div>
                {rootCauseAddError ? <p className="mt-2 text-sm text-rose-600">{rootCauseAddError}</p> : null}
              </div>
            ) : null}

            <div>
              <Label>Resolution notes</Label>
              <TextArea
                value={resolutionNotes}
                disabled={!writesAllowed || incident.status === "resolved" || savedRepairGuidance != null}
                onChange={(event) => setResolutionNotes(event.target.value)}
                className="mt-1 font-sans"
              />
            </div>

            {repairPreviewGuidance && savedRepairGuidance == null ? (
              <RepairReturnCallout guidance={repairPreviewGuidance} />
            ) : null}

            {unresolvedPottingRework ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                This incident is waiting for potting rework regrade before it can be resolved.
              </p>
            ) : null}

            <div className="flex justify-end">
              <Button type="submit" disabled={submitDisabled} isLoading={resolveMutation.isPending}>
                {savedRepairGuidance != null ? "Saved" : "Save resolution"}
              </Button>
            </div>
          </form>
        </ReadOnlyHoverLayer>
      </CardBody>
    </Card>
  );
}
